/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2020 Open Assessment Technologies SA;
 */

import { debounce } from 'lodash';
import patternMatch from '../pattern/match.js';

/**
 * Realize part of interface ValidityState {@see https://developer.mozilla.org/en-US/docs/Web/API/ValidityState}
 *
 * @example of using validation if pattern check is not required:
 * {
 *     const validation = new Validity();
 *     validation.setRequired(true).setLongLimitation(50).setShortLimitation(2);
 *     const result = validation.get(value);
 * }
 *
 * @example of using validation if pattern check required:
 * {
 *     const validation = new Validity();
 *     validation.setRequired(true).setPattern('[A-Za-z]');
 *     validation.run(value)
 *         .then(validity => {
 *             const result = validity;
 *         });
 * }
 */
export default function Validity() {
    const validityChecks = Object.freeze({
        patternMismatch: 'patternMismatch',
        tooLong: 'tooLong',
        tooShort: 'tooShort',
        valueMissing: 'valueMissing',
        valid: 'valid'
    });
    let isRequired = false;
    let longLimit = 0; // unlimited
    let shortLimit = 0; // unlimited
    let pattern = '';
    this.patternMatchTerminate = null; // previous pattern match process

    /**
     * Set constraint that value match pattern
     * @param {string} expression
     * @returns {default}
     */
    this.setPattern = function (expression) {
        if (this.patternMatchTerminate) {
            this.patternMatchTerminate();
        }
        if (isString(expression)) {
            this.debouncedMatchPattern = debounce(this.matchPattern, 400, { maxWait: 800 });
            pattern = expression;
        }
        return this;
    };

    /**
     * Set constraint that value length less than expected
     * @param {int} limit
     * @returns {default}
     */
    this.setLongLimitation = function (limit) {
        if (Number.isInteger(limit)) {
            longLimit = limit;
        }
        return this;
    };

    /**
     * Set constraint that value length more than expected
     * @param {int} limit
     * @returns {default}
     */
    this.setShortLimitation = function (limit) {
        if (Number.isInteger(limit)) {
            shortLimit = limit;
        }
        return this;
    };

    /**
     * Set constraint that value is not empty
     * @param {boolean} require
     * @returns {default}
     */
    this.setRequired = function (require) {
        if (typeof require === 'boolean') {
            isRequired = require;
        }
        return this;
    };

    /**
     * Return validity state if pattern not set for 'patternMismatch'
     * @param {string} value
     * @returns {{}}
     */
    this.get = function (value) {
        const validity = {
            [validityChecks.valueMissing]: false,
            [validityChecks.patternMismatch]: false,
            [validityChecks.tooLong]: false,
            [validityChecks.tooShort]: false
        };
        if (!isString(value)) {
            validity[validityChecks.valid] = true;
            return validity;
        }

        validity[validityChecks.valueMissing] = isRequired && value.length === 0;
        validity[validityChecks.patternMismatch] = false;
        validity[validityChecks.tooLong] = longLimit > 0 && value.length > longLimit;
        validity[validityChecks.tooShort] = shortLimit > 0 && value.length < shortLimit;
        validity[validityChecks.valid] = Object.values(validity).every(check => !check);

        return validity;
    };

    /**
     * Return validity state with value tested by pattern
     * @param {string} value
     * @returns {Promise<unknown>}
     */
    this.run = value =>
        // eslint-disable-next-line implicit-arrow-linebreak
        new Promise(resolve => {
            if (!isString(value)) {
                resolve(this.get(value));
            }
            this.debouncedMatchPattern(pattern, value, (err, result) => {
                const validity = this.get(value);

                validity[validityChecks.patternMismatch] = err ? false : result === null;
                validity[validityChecks.valid] =
                    validity[validityChecks.valid] && !validity[validityChecks.patternMismatch];

                resolve(validity);
            });
        });

    /**
     * Stop worker
     */
    this.stop = function () {
        if (this.patternMatchTerminate) {
            this.patternMatchTerminate();
        }
    };

    /**
     * Run worker for testing pattern
     * @param {string} expression - regular expression for testing text
     * @param {string} text - subject for test
     * @param {function} callback - called when worker return result of testing 'text' by 'expression'
     */
    this.matchPattern = function (expression, text, callback) {
        if (typeof this.patternMatchTerminate === 'function') {
            this.patternMatchTerminate();
        }

        this.patternMatchTerminate = patternMatch({ pattern: new RegExp(expression), text, timeout: 1000 }, callback);
    };
}

/**
 * Check if input value is string
 * @param {any} str
 * @returns {boolean}
 */
function isString(str) {
    return Object.prototype.toString.call(str) === '[object String]';
}
